import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import TeeCard from '../components/TeeCard';
import PostTeeTimeModal from '../components/PostTeeTimeModal';
import TeeDetailModal from '../components/TeeDetailModal';
import WelcomeModal, { hasSeenWelcome } from '../components/WelcomeModal';
import { EVERYONE_GROUP, APP_WIDE_GROUP } from '../constants';

const FILTERS = ['All', 'Open spots', 'My groups', 'Weekend', 'Morning'];

// User's app-wide radius override for the "Show more further away" expander.
// We bump from the user's saved radius (default 50) to this expanded radius
// when they click "Show N more" — covers larger trips without permanently
// widening their default.
const EXPANDED_RADIUS_MI = 200;

export default function BrowsePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [teeTimes, setTeeTimes] = useState([]);
  const [filter, setFilter] = useState('All');
  const [groupFilter, setGroupFilter] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPost, setShowPost] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [expandedRadius, setExpandedRadius] = useState(false);

  // Anchor state — derived from /me. Determines whether we show the "Set your
  // home region" CTA banner and whether App-Wide cards make it through the
  // server at all.
  const hasAnchor = !!(user?.home_lat && user?.home_lng);

  useEffect(() => {
    if (!hasSeenWelcome()) setShowWelcome(true);
  }, []);

  const load = useCallback(async (opts = {}) => {
    try {
      const data = await api.getTeeTimes(opts);
      setTeeTimes(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(expandedRadius ? { radiusMi: EXPANDED_RADIUS_MI } : {});
    api.getGroups().then(setGroups).catch(() => {});
  }, [load, expandedRadius]);

  const filtered = teeTimes.filter(tt => {
    if (groupFilter === EVERYONE_GROUP.id) {
      if (!tt.is_everyone) return false;
    } else if (groupFilter === APP_WIDE_GROUP.id) {
      if (!tt.is_app_wide) return false;
    } else if (groupFilter) {
      if (!(tt.groups || []).some(g => g.id === groupFilter)) return false;
    }
    if (filter === 'Open spots') return (tt.total_spots - tt.player_count) > 0;
    if (filter === 'My groups')  return (tt.groups || []).length > 0;
    if (filter === 'Weekend') {
      const rawDate = tt.tee_date.includes('T') ? tt.tee_date.split('T')[0] : tt.tee_date;
      const [yr, mo, dy] = rawDate.split('-').map(Number);
      const d = new Date(yr, mo - 1, dy);
      return d.getDay() === 0 || d.getDay() === 6;
    }
    if (filter === 'Morning') return parseInt(tt.tee_time) < 12;
    return true;
  });

  const detailTT = teeTimes.find(tt => tt.id === detailId);

  // Count of App-Wide cards in the current view — used to decide whether the
  // "Show more further away" expander makes sense to show.
  const appWideCount = teeTimes.filter(tt => tt.is_app_wide).length;

  return (
    <div>
      <div className="sh" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:'1.1rem', flexWrap:'wrap', gap:'.75rem' }}>
        <div>
          <h2 style={{ fontSize: 22 }}>Upcoming tee times</h2>
          <p style={{ fontSize: 12, color: '#7a7a6a', marginTop: 1 }}>Find a group and get out on the course</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowPost(true)}>+ Post tee time</button>
      </div>

      {/* "Set your home region" CTA — only shown to users without an anchor.
          Without one, App-Wide tee times don't appear at all (server-side filter),
          so we surface the prompt prominently. Dismiss is intentionally absent —
          the banner disappears as soon as they set a region. */}
      {!hasAnchor && (
        <div style={{
          background: '#faf6e8', border: '1px solid #ebe0bd', borderRadius: 9,
          padding: '0.7rem 0.9rem', marginBottom: '0.75rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 10, flexWrap: 'wrap',
        }}>
          <div style={{ fontSize: 13, color: '#7a5a0a', lineHeight: 1.45 }}>
            <strong>📡 See App-Wide tee times near you</strong>
            <div style={{ fontSize: 12, color: '#9a8a5a', marginTop: 2 }}>
              Set your home region so we can show open tee times within your radius.
            </div>
          </div>
          <button
            className="btn btn-sm"
            style={{ background: '#7a5a0a', color: '#faf6e8', border: 'none' }}
            onClick={() => navigate('/profile')}
          >
            Set home region
          </button>
        </div>
      )}

      <div style={{ marginBottom: '0.75rem' }}>
        <select
          style={{ fontSize: 12, padding: '5px 10px', borderRadius: 20, border: '1.5px solid #d8d4c8', background: '#fff', color: '#0d2b1a', cursor: 'pointer' }}
          value={groupFilter}
          onChange={e => setGroupFilter(e.target.value)}
        >
          <option value="">All groups</option>
          <option value={EVERYONE_GROUP.id}>{EVERYONE_GROUP.emoji} {EVERYONE_GROUP.name}</option>
          <option value={APP_WIDE_GROUP.id}>{APP_WIDE_GROUP.emoji} {APP_WIDE_GROUP.name}</option>
          {groups.map(g => <option key={g.id} value={g.id}>{g.emoji} {g.name}</option>)}
        </select>
      </div>

      <div className="filter-row">
        {FILTERS.map(f => (
          <button key={f} className={`filter-chip${filter === f ? ' active' : ''}`} onClick={() => setFilter(f)}>{f}</button>
        ))}
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading tee times…</p></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <p style={{ fontSize: 28 }}>⛳</p>
          <p>No tee times match this filter.</p>
        </div>
      ) : (
        <div className="tee-grid">
          {filtered.map(tt => (
            <TeeCard key={tt.id} tt={tt} currentUser={user} onRefresh={load} onViewThread={() => setDetailId(tt.id)} />
          ))}
        </div>
      )}

      {/* "Show more further away" expander — shown only when we have an anchor
          AND at least one App-Wide card is already visible (signal that App-Wide
          is working for this user). Bumps radius from the user's default to
          EXPANDED_RADIUS_MI for the duration of the page. */}
      {hasAnchor && !loading && appWideCount > 0 && (
        <div style={{ marginTop: '0.9rem', textAlign: 'center' }}>
          {!expandedRadius ? (
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setExpandedRadius(true)}
              style={{ fontSize: 12 }}
            >
              📡 Show more App-Wide tee times further away ({EXPANDED_RADIUS_MI} mi)
            </button>
          ) : (
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setExpandedRadius(false)}
              style={{ fontSize: 12 }}
            >
              Collapse to your default radius
            </button>
          )}
        </div>
      )}

      {showPost && <PostTeeTimeModal onClose={() => setShowPost(false)} onCreated={() => { setShowPost(false); load(); }} />}
      {detailTT && <TeeDetailModal tt={detailTT} currentUser={user} onClose={() => setDetailId(null)} onRefresh={load} />}
      {showWelcome && <WelcomeModal onClose={() => setShowWelcome(false)} />}
    </div>
  );
}
